import { jobApplyWithSonicApplyButtonClickedEvent } from '@lib/analytics-events/job'
import { useJobStore } from '@stores/job.store'
import { useCallback } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { useSearchStore } from '@stores/search.store'
import { JobItem } from '@types'
import { match } from 'ts-pattern'

const SONICJOBS_PARTNER_CODE = 'sjw'
const LOCAL_HOST = 'easy-apply-micro.sonic-local.com'
const FEATURE_WEB_HOST = /^([^.]+)\.feature-web\.sonic-dev\.net$/

// Feature environments reuse the main-branch image, so the host is read from the page at click
// time: <MR>.feature-web.sonic-dev.net opens easy-apply-micro-<MR>.feature.sonic-dev.net. [S20-1290]
function getFeatureHost(): string {
  const featureSubdomain =
    typeof window !== 'undefined' ? window.location.hostname.match(FEATURE_WEB_HOST)?.[1] : undefined
  return featureSubdomain ? `easy-apply-micro-${featureSubdomain}.feature.sonic-dev.net` : LOCAL_HOST
}

export default function useApplyWithSonicApply(): () => void {
  const [job] = useJobStore(useShallow((state) => [state.job]))
  const [search] = useSearchStore(useShallow((state) => [state.search, state.serpPositionClicked]))
  const locale = 'us'
  const environment = process.env.NEXT_PUBLIC_ENVIRONMENT || 'local'

  return useCallback(() => {
    jobApplyWithSonicApplyButtonClickedEvent(job as unknown as JobItem, search)
    if (job) {
      const baseUrl = match(environment)
        .with('production', () => 'apply.sonicjobs.com')
        .with('local', () => LOCAL_HOST)
        .otherwise(getFeatureHost)
      // Read at click time rather than mount time: this is a click handler, so the query string it
      // needs to carry is whatever is on the page right now, not whatever it was when the hook first
      // rendered. Removes the state + effect that used to mirror it. [S20-865]
      const sp = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).toString() : ''
      const sonicApplyJobUrl = `https://${baseUrl}/${locale}/${SONICJOBS_PARTNER_CODE}/${job.id}?${sp}`
      window.open(sonicApplyJobUrl, '_blank')
    }
  }, [job, search, environment, locale])
}
